import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Checkbox, Autocomplete, Typography
} from '@mui/material';
import {countries} from "../../../utils/countries";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {REGIONS} from "../../../utils/META/cat_101";
import {FormControl} from "@material-ui/core";
import {isValidEmail} from "../../../utils/utils";
import {LANGUAGES_SUPPORTED} from "../../../utils/constants";

const defaultFields = {
    org_name: '',
    contact_name: '',
    contact_email: '',
    preferred_locale: '',
    org_address: '',
    org_city: '',
    postal_code: '',
    org_country: '',
    callback_url: 'To be Decided',
    callback_key: 'To be Decided',
};

const CreateClientDialog = ({ open, onClose, onSubmit, loading }) => {
    const [fields, setFields] = useState(defaultFields);
    const [errors, setErrors] = useState({});
    const [apiAccess, setApiAccess] = useState(false);

    useEffect(() => {
        if (open) {
            setFields(defaultFields);
            setErrors({});
            setApiAccess(false);
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFields(f => ({ ...f, [name]: value }));
    };

    const handleCreate = () => {
        let newErrors = {};
        if (!fields.org_name) newErrors.org_name = "Required";
        if (!fields.contact_name) newErrors.contact_name = "Required";
        if (!fields.contact_email) {
            newErrors.contact_email = "Required";
        } else if (!isValidEmail(fields.contact_email)) {
            newErrors.contact_email = "Invalid email address";
        }

        if (apiAccess) {
            if (!fields.callback_url) newErrors.callback_url = "Required";
            if (!fields.callback_key) newErrors.callback_key = "Required";
        }
        if (!fields.org_country) newErrors.org_country = "Required";
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
            const payload = {
                ...fields,
                ...(apiAccess ? {} : { callback_url: '', callback_key: '' }),
            };
            console.log("Submitting payload:", payload);
            onSubmit(payload);
        }
    };

    const handleClose = () => {
        setFields(defaultFields);
        setErrors({});
        setApiAccess(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm"
                PaperProps={{
                    sx: {
                        border: '2px solid #000000',
                        borderRadius: '15px',
                        overflow: {xs:'scroll',sm:'hidden'},
                    }
                }}>
            <DialogTitle sx={{ color: '#ff6468',fontsize:'43px', fontWeight: 'bold', textAlign: 'center' }}>
                Add a New Client
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ textAlign: 'center', fontSize: '17px', mb: 2 }}>
                    Fill in the following fields about your client.
                </Typography>
                <Box sx={{ my: 2 }}>
                    <TextField
                        label="Organization Name"
                        name="org_name"
                        value={fields.org_name}
                        onChange={handleChange}
                        error={!!errors.org_name}
                        helperText={errors.org_name}
                        fullWidth
                        sx={{ mb: 2 }}
                        autoFocus
                    />
                    <TextField
                        label="Contact Person Name"
                        name="contact_name"
                        value={fields.contact_name}
                        onChange={handleChange}
                        error={!!errors.contact_name}
                        helperText={errors.contact_name}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Contact Person Email"
                        name="contact_email"
                        value={fields.contact_email}
                        onChange={handleChange}
                        error={!!errors.contact_email}
                        helperText={errors.contact_email}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        select
                        label="Preferred Language"
                        name="preferred_locale"
                        value={fields.preferred_locale}
                        onChange={handleChange}
                        error={!!errors.preferred_locale}
                        helperText={errors.preferred_locale || ' '}
                        fullWidth
                        // sx={{ mb: 2 }}
                    >
                        {Object.entries(LANGUAGES_SUPPORTED).map(([code, name]) => (
                            <MenuItem key={code} value={code}>
                                {name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Organization Address (optional)"
                        name="org_address"
                        value={fields.org_address}
                        onChange={handleChange}
                        error={!!errors.org_address}
                        helperText={errors.org_address}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Organization City (optional)"
                        name="org_city"
                        value={fields.org_city}
                        onChange={handleChange}
                        error={!!errors.org_city}
                        helperText={errors.org_city}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Organization Postal Code (optional)"
                        name="postal_code"
                        value={fields.postal_code}
                        onChange={handleChange}
                        error={!!errors.postal_code}
                        helperText={errors.postal_code}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    <Autocomplete
                        options={countries}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) => option.code === value?.code}
                        value={countries.find((option) => option.code === fields.org_country) || null}
                        onChange={(event, newValue) => {
                            setFields((f) => ({
                                ...f,
                                org_country: newValue?.code || "",
                            }));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Organization Country"
                                variant="outlined"
                                error={!!errors.org_country}
                                helperText={errors.org_country || " "}
                            />
                        )}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    <FormControlLabel
                        sx={{ mb: 2, mt: 1 }}
                        control={
                            <Checkbox
                                checked={apiAccess}
                                onChange={(e) => setApiAccess(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Check here if this client needs access to the Assessment via API"
                    />
                    {apiAccess && (
                        <>
                            <TextField
                                label="Callback URL"
                                name="callback_url"
                                value={fields.callback_url}
                                onChange={handleChange}
                                error={!!errors.callback_url}
                                helperText={errors.callback_url}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Callback Key"
                                name="callback_key"
                                value={fields.callback_key}
                                onChange={handleChange}
                                error={!!errors.callback_key}
                                helperText={errors.callback_key}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Box sx={{ display: 'flex', width: '100%',alignItems:'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderRadius: '20px',
                            minWidth: '120px',
                            fontWeight: 'bold',
                            borderColor: '#0097b2',
                            color: '#0097b2',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        sx={{
                            backgroundColor: '#ff6468',
                            color: '#fff',
                            borderRadius: '20px',
                            minWidth: '120px',
                            fontWeight: 'bold',
                            '&:hover': {
                                backgroundColor: '#ff6468',
                                color: '#ffffff',
                            },
                        }}
                        disabled={loading}
                    >
                        {loading ? 'Creating...' : 'Create'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default CreateClientDialog;